/**
 * The typed client for print and email templates (Admin › Print Templates,
 * Admin › Email Templates, the Print and Send buttons on a document).
 *
 * Kept beside api.ts rather than inside it: the template screens are the only
 * readers, and every call goes through `api.call`, so identity, the company
 * header and error envelopes are handled exactly as everywhere else.
 */
import type { Api } from './api.js'

export type TemplateMode = 'HANDLEBARS' | 'PUG'

export interface PrintTemplateRow {
  readonly id: string
  readonly docType: string
  readonly docTypeLabel: string
  readonly name: string
  readonly fileName: string | null
  readonly isStandard: boolean
  readonly isDefault: boolean
  readonly active: boolean
  readonly appliesTo: 'ALL' | 'SELECTED'
  readonly companyIds: readonly string[]
  readonly companyNames: readonly string[]
  readonly mode: TemplateMode
  readonly editable: boolean
  readonly createdAt: string | null
  readonly createdByName: string | null
  readonly updatedAt: string | null
  readonly updatedByName: string | null
}

export interface PrintTemplateDetail extends PrintTemplateRow {
  readonly body: string
}

export interface LayoutChoice {
  readonly id: string
  readonly name: string
  readonly isStandard: boolean
  readonly isDefault: boolean
}

export interface SavePrintTemplate {
  readonly id?: string
  readonly docType: string
  readonly name: string
  readonly fileName: string | null
  readonly body: string
  readonly isDefault: boolean
  readonly appliesTo: 'ALL' | 'SELECTED'
  readonly companyIds: readonly string[]
  readonly active: boolean
  readonly mode: TemplateMode
}

/** A shipped layout's id carries a slash, so it is always encoded in a path. */
const one = (id: string): string => `/templates/one/${encodeURIComponent(id)}`

export const printTemplatesApi = (api: Api) => ({
  rows: () => api.call<readonly PrintTemplateRow[]>('/templates'),
  get: (id: string) => api.call<PrintTemplateDetail>(one(id)),
  standard: (docType: string) => api.call<{ body: string }>(`/templates/standard?docType=${encodeURIComponent(docType)}`),
  choices: (docType: string) => api.call<readonly LayoutChoice[]>(`/templates/for/${docType}`),
  save: (input: SavePrintTemplate) => api.call<PrintTemplateRow>('/templates', { body: input }),
  copy: (id: string, name?: string) => api.call<PrintTemplateRow>(`${one(id)}/copy`, { body: name ? { name } : {} }),
  setActive: (id: string, active: boolean) => api.call<PrintTemplateRow>(`${one(id)}/active`, { body: { active } }),
  makeDefault: (id: string) => api.call<PrintTemplateRow>(`${one(id)}/default`, { body: {} }),
  remove: (id: string) => api.call<void>(`${one(id)}/delete`, { body: {} }),
  preview: (docType: string, body: string, mode: TemplateMode) =>
    api.callText('/templates/preview', { body: { docType, body, mode } }),
  exportUrl: (ids: readonly string[]) => `/api/templates/export?ids=${encodeURIComponent(ids.join(','))}`,
})

/* -- document email templates (PRN-047, PRN-048) ------------------------- */

export type FromMode = 'SENDER' | 'COMPANY'

export interface MailTemplateRow {
  readonly id: string
  readonly docType: string
  readonly docTypeLabel: string
  readonly name: string
  readonly isStandard: boolean
  readonly isDefault: boolean
  readonly active: boolean
  readonly appliesTo: 'ALL' | 'SELECTED'
  readonly companyIds: readonly string[]
  readonly companyNames: readonly string[]
  readonly fromMode: FromMode
  readonly cc: readonly string[]
  readonly bcc: readonly string[]
  readonly ccProjectRoles: readonly string[]
  readonly bccProjectRoles: readonly string[]
  readonly senderInBcc: boolean
  readonly attachPrint: boolean
  readonly subject: string
  readonly body: string
  readonly bodyIsHtml: boolean
  readonly editable: boolean
  readonly createdAt: string | null
  readonly createdByName: string | null
}

export type SaveMailTemplate = Omit<MailTemplateRow,
  'id' | 'docTypeLabel' | 'isStandard' | 'companyNames' | 'bodyIsHtml' | 'editable' | 'createdAt' | 'createdByName'> & { readonly id?: string }

const mailOne = (id: string): string => `/mail/document-templates/${encodeURIComponent(id)}`

export const mailTemplatesApi = (api: Api) => ({
  rows: () => api.call<readonly MailTemplateRow[]>('/mail/document-templates'),
  choices: (docType: string) => api.call<readonly LayoutChoice[]>(`/mail/document-templates/for/${docType}`),
  save: (input: SaveMailTemplate) => api.call<MailTemplateRow>('/mail/document-templates', { body: input }),
  copy: (id: string) => api.call<MailTemplateRow>(`${mailOne(id)}/copy`, { body: {} }),
  setActive: (id: string, active: boolean) => api.call<MailTemplateRow>(`${mailOne(id)}/active`, { body: { active } }),
  makeDefault: (id: string) => api.call<MailTemplateRow>(`${mailOne(id)}/default`, { body: {} }),
  remove: (id: string) => api.call<{ ok: true }>(`${mailOne(id)}/delete`, { body: {} }),
  send: (documentId: string, choice: { mailTemplateId?: string; printTemplateId?: string }) =>
    api.call<{ to: string; queued: boolean; attachment: string | null }>(`/mail/send/${documentId}`, { body: choice }),
  capabilities: () => api.call<{ pdf: boolean; transportConfigured: boolean }>('/mail/capabilities'),
  exportUrl: (ids: readonly string[]) => `/api/mail/document-templates/export?ids=${encodeURIComponent(ids.join(','))}`,
})

/** Every placeholder a document email may use, as the editor lists them. */
export const MAIL_PLACEHOLDERS = [
  'company__legal_name', 'doc_no', 'doc_date', 'doc_type', 'grand_total', 'supplier__label', 'customer__label',
  'party__label', 'project__label', 'delivery_date', 'due_date', 'sender_name', 'sender_designation',
  'sender_mobile', 'sender_email',
] as const
